import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
} from '@angular/common/http';

import { Store } from '@ngrx/store';
import { OidcSecurityService } from 'angular-auth-oidc-client';

import { EMPTY, Observable, of, throwError } from 'rxjs';
import { catchError, filter, first, mergeMap, switchMap } from 'rxjs/operators';
import { concatLatestFrom } from '@ngrx/operators';

import { environment } from '@environments/environment';
import {
  selectCurrentToken,
  selectAuthState,
  PLLoginRedirect,
  PLCheckUserStatus,
} from '../store';
import { PlImpersonationService } from './pl-impersonation.service';
import { PlAuthContextService } from './pl-auth-context.service';

// OIDC endpoints are public and should never have auth headers added
const OIDC_BASE_URL = `${environment.sirius_url}/oidc`;

// Session check endpoint uses session cookies only, not access tokens.
// Adding Authorization header causes Antares to validate the token, which can trigger
// 401 → token refresh → SSO redirect, creating race conditions with other tabs.
const SESSION_CHECK_URL = `${environment.sirius_url}/auth/session/check`;

// Django status endpoint sits outside the gateway, on the login host, but is
// still allowed on this middleware so a 401 response drives its session handling in
// handle401() for the Django path.
const DJANGO_STATUS_URL = `${environment.django_login_url}/api/v1/status/`;

// LearnUpon SSO setup also sits on the login host. It needs the access token so
// the login service can identify the caller and mint the SSO context.
const LEARNUPON_SSO_SETUP_PATH = '/saml/sso/setup_learnupon_sso/';

// Header set by edge-gateway when token is revoked (not just expired)
const TOKEN_REVOKED_HEADER = 'X-Token-Revoked';

// Key must match auth-sync.service.ts
const APP_NAMESPACE = (window as any).AUTH_CONSTANTS?.APP_NAMESPACE || 'te';
const RECENTLY_LOGGED_OUT_KEY = `${APP_NAMESPACE}_recently_logged_out`;

@Injectable()
export class PlAuthInterceptor implements HttpInterceptor {
  constructor(
    private store: Store,
    private oidcSecurityService: OidcSecurityService,
    private impersonationService: PlImpersonationService,
    private authContextService: PlAuthContextService
  ) {}

  // Check if 401 is due to token revocation (vs expiration)
  private isTokenRevoked(error: HttpErrorResponse): boolean {
    return error.headers.get(TOKEN_REVOKED_HEADER) === 'true';
  }

  private handleTokenRevocation(): Observable<never> {
    // eslint-disable-next-line no-console
    console.warn(
      '[PlAuthInterceptor] Token revoked by server - forcing logout'
    );

    // CRITICAL: Record the logout timestamp FIRST before any redirects
    // This prevents checkAuth() from silently re-authenticating
    localStorage.setItem(RECENTLY_LOGGED_OUT_KEY, Date.now().toString());

    // Broadcast logout to other tabs so they don't share stale tokens
    // This prevents other tabs from responding to REQUEST_AUTH_STATE with revoked tokens
    try {
      const channel = new BroadcastChannel('pl_auth_channel');
      channel.postMessage({
        type: 'LOGOUT_INIT',
        tabId: 'interceptor',
        timestamp: Date.now(),
      });
      channel.close();
    } catch (e) {
      // BroadcastChannel not supported or failed - continue with local logout
    }

    // Clear local OIDC state without server round-trip
    this.impersonationService.stop();
    this.oidcSecurityService.logoffLocal();

    // Redirect to logged-out page instead of home to prevent auto-login loop
    window.location.href = '/auth/logged-out';
    return EMPTY;
  }

  intercept(
    httpRequest: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Security: prevent credential disclosure to an unauthorized third party.
    // The access token goes to the API gateway, plus two login-host endpoints:
    // Django status and LearnUpon SSO setup. Third-party APIs such as Klipy,
    // and server-supplied destinations such as S3 upload URLs, must never see it.
    // Never compare hostnames with string prefixes: `startsWith` would let https://api.presence.com.evil.com through.
    let requestUrl: URL | null = null;
    try {
      requestUrl = new URL(httpRequest.url);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        `[PlAuthInterceptor] Unparseable request url, withholding credentials: ${httpRequest.url}`,
        error
      );
      return next.handle(httpRequest);
    }

    // Matched on hostname and protocol. Read per request rather than once at
    // module load, so the gateway host can be varied in tests.
    // Keeping protocol in the comparison still withholds the token from a plaintext http downgrade.
    const gatewayUrl = new URL(environment.apps.gateway);
    const isGatewayUrl =
      requestUrl?.hostname === gatewayUrl.hostname &&
      requestUrl?.protocol === gatewayUrl.protocol;

    // Same host, port, and protocol comparison as the gateway. Read per request
    // so tests can vary the login host. A prefixed host such as
    // login.presence.com.evil.com does not match.
    const loginUrl = new URL(environment.django_login_url);
    const isLearnUponSsoSetup =
      requestUrl?.hostname === loginUrl.hostname &&
      requestUrl?.port === loginUrl.port &&
      requestUrl?.protocol === loginUrl.protocol &&
      requestUrl?.pathname === LEARNUPON_SSO_SETUP_PATH;

    if (
      !isGatewayUrl &&
      !httpRequest.url.startsWith(DJANGO_STATUS_URL) &&
      !isLearnUponSsoSetup
    ) {
      return next.handle(httpRequest);
    }

    // Skip OIDC endpoints - they're public and should never have auth headers
    // This prevents infinite loops when token is revoked and we try to re-authorize
    if (httpRequest.url.startsWith(OIDC_BASE_URL)) {
      return next.handle(httpRequest);
    }

    // Skip session check endpoint - it validates session cookies, not access tokens.
    // Including the Authorization header would cause Antares to validate the token,
    // potentially triggering 401 → refresh → SSO flows that create race conditions
    // when RenewToken() invalidates session tokens for other tabs' in-flight requests.
    if (httpRequest.url.startsWith(SESSION_CHECK_URL)) {
      return next.handle(httpRequest);
    }

    return this.authContextService.getAccessToken(httpRequest).pipe(
      mergeMap((token) => {
        let nextReq = httpRequest;

        if (token) {
          nextReq = this.addTokenToReq(httpRequest, token);
        }

        return next.handle(nextReq).pipe(
          catchError((error) => {
            if (
              error instanceof HttpErrorResponse &&
              error.status === 401 &&
              token
            ) {
              // Check if token was revoked - force logout, don't try refresh
              if (this.isTokenRevoked(error)) {
                return this.handleTokenRevocation();
              }
              // If we're impersonating and get a 401, the impersonation token is invalid
              // Check impersonation state from the store
              return this.authContextService.isImpersonating().pipe(
                first(),
                switchMap((isImpersonating) => {
                  if (isImpersonating) {
                    this.impersonationService.stop();
                    // Reload the page to root in case the user was on a page they no longer have access to
                    window.location.href = '/';
                    return EMPTY;
                  }
                  return this.handle401(next, nextReq);
                })
              );
            }
            return throwError(error);
          })
        );
      })
    );
  }

  private isStatusRequest(httpRequest: HttpRequest<any>) {
    return httpRequest.url.includes('/api/v1/status/');
  }

  private handle401(next: HttpHandler, httpRequest: HttpRequest<any>) {
    // This method only handles 401s when authenticated using Django Auth.
    // Sirius has a separate refresh mechanism, where you should always have a valid access token.
    if (this.isStatusRequest(httpRequest)) {
      this.store.dispatch(PLLoginRedirect({ url: window.location.pathname }));
      return EMPTY;
    }

    return this.handleRefresh(next, httpRequest);
  }

  private handleRefresh(next: HttpHandler, httpRequest: HttpRequest<any>) {
    // This is only used for Django Auth refreshes
    this.store.dispatch(PLCheckUserStatus({ lastActive: 0 }));

    return this.getAccessToken().pipe(
      mergeMap((newToken) =>
        next.handle(this.addTokenToReq(httpRequest, newToken))
      )
    );
  }

  private addTokenToReq(httpRequest: HttpRequest<any>, token: string) {
    return httpRequest.clone({
      setHeaders: { authorization: `JWT ${token}` },
    });
  }

  private getAccessToken() {
    // This is only used for Django Auth refreshes
    // For API requests, check PLAuthContextService.getAccessToken
    return this.store.select(selectAuthState).pipe(
      filter((authState) => authState !== 'pending'),
      concatLatestFrom(() => this.store.select(selectCurrentToken)),
      switchMap(([, token]) => of(token)),
      first()
    );
  }
}
